import { useEffect, useState } from 'react';

/**
 * Live-ticking countdown. Decrements once per second.
 * - When `seconds` is undefined → returns null (e.g. while loading).
 * - When seconds reaches 0, stays at 0 until the source refreshes.
 * - When `seconds` changes (e.g. after a refetch), resets to the new value.
 *
 * Pair with TanStack Query refetchInterval (~30s) for the source so that
 * after period rollover the timer resyncs.
 */
export function useCountdown(seconds: number | undefined): number | null {
  const [remaining, setRemaining] = useState<number | null>(
    seconds === undefined ? null : Math.max(0, seconds)
  );

  // Resync when the source value changes (period rollover, manual refetch).
  useEffect(() => {
    setRemaining(seconds === undefined ? null : Math.max(0, seconds));
  }, [seconds]);

  // Tick once per second while > 0.
  useEffect(() => {
    if (remaining === null || remaining <= 0) return;
    const id = setInterval(() => {
      setRemaining((cur) => (cur === null ? null : Math.max(0, cur - 1)));
    }, 1000);
    return () => clearInterval(id);
  }, [remaining === null, remaining === 0]);

  return remaining;
}

/** Format a seconds count as HH:MM:SS (or MM:SS if under an hour). */
export function formatCountdown(s: number | null): string {
  if (s === null) return '—';
  const sec = Math.max(0, Math.floor(s));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const ss = sec % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(ss)}` : `${pad(m)}:${pad(ss)}`;
}
